import { EditOutlined, ExportOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link as LinkRouterDom, useSearchParams } from 'react-router';

import { type RootState } from '../reducers';
import { SPACING } from '../theme/tokens';

export function getLinkLocation(
  to: string,
  searchParams: URLSearchParams,
): { pathname: string; search: string } {
  const [pathname, query = ''] = to.split('?');
  const linkSearchParams = new URLSearchParams(searchParams);

  new URLSearchParams(query).forEach((value: string, key: string): void => {
    linkSearchParams.set(key, value);
  });

  return { pathname, search: linkSearchParams.toString() };
}

function Link({
  to,
  label,
  openInNewTab,
  disabled = false,
  additionalSearchParams = {},
}: {
  to: string;
  label?: string | React.ReactElement;
  openInNewTab?: boolean;
  disabled?: boolean;
  additionalSearchParams?: Record<string, string>;
}): React.ReactElement {
  const languageTypeCode = useSelector(
    (s: RootState) => s.Language.languageType?.code,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect((): void => {
    if (!languageTypeCode) {
      return;
    }

    searchParams.set('languageTypeCode', languageTypeCode);

    setSearchParams(searchParams);
  }, [languageTypeCode]);

  const additionalParamsKey: string = Object.entries(
    additionalSearchParams ?? {},
  )
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');

  useEffect((): void => {
    if (!additionalParamsKey) {
      return;
    }

    for (const key in additionalSearchParams) {
      if (Object.prototype.hasOwnProperty.call(additionalSearchParams, key)) {
        searchParams.set(key, additionalSearchParams[key]);
      }
    }

    setSearchParams(searchParams);
  }, [additionalParamsKey]);

  let renderedLabel: string | React.ReactElement | undefined = label;

  if (openInNewTab && label) {
    renderedLabel = (
      <Space size={SPACING.sm} align="start">
        {label}
        <ExportOutlined />
      </Space>
    );
  } else if (openInNewTab && !label) {
    renderedLabel = <ExportOutlined />;
  } else if (!openInNewTab && label) {
    renderedLabel = label;
  } else if (!openInNewTab && !label) {
    renderedLabel = <EditOutlined />;
  }

  if (disabled) {
    if (openInNewTab) {
      return <ExportOutlined disabled />;
    }

    return <EditOutlined disabled />;
  }

  return (
    <LinkRouterDom
      to={getLinkLocation(to, searchParams)}
      target={openInNewTab ? '_blank' : '_self'}
      rel={openInNewTab ? 'noopener noreferrer nofollow' : undefined}
    >
      {renderedLabel}
    </LinkRouterDom>
  );
}

export default Link;
